import {
  forwardRef,
  useEffect,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsButton from '../LdsButton/index.jsx';
import LdsIcon from '../LdsIcon/index.jsx';
import LdsLoadingSpinner from '../LdsLoadingSpinner/index.jsx';
import SearchInput from './SearchInput.jsx';
import useOutsideClick from '../../util/useOutsideClick.js';

const LdsSearch = forwardRef(function LdsSearch({
  autofocus = true,
  clearBtnAriaLabel = 'Clear search',
  value = '',
  disabled = false,
  id,
  inputAriaDescription = 'Suggestions will update as you type',
  inputAriaLabel = 'Search the site',
  isLoading = false,
  onChange = () => {},
  onFocus = () => {},
  onInput = () => {},
  onSubmit = () => {},
  loadingBtnAriaLabel = 'Loading results...',
  placeholder = 'Type search term...',
  searchSuggestions = [],
  submitBtnAriaLabel = 'Submit search',
  suggestionsAriaLabel = `Search suggestions. ${searchSuggestions.length} results. Use up and down arrows to review.`,
  isTypeAhead = false,
  className,
  searchInputClassName,
  searchInputAttributes,
  ...rest
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAutosuggestEnabled, setIsAutosuggestEnabled] = useState(isTypeAhead);
  const [focusIndex, setFocusIndex] = useState(-1);

  const inputRef = useRef();
  const suggestionsRef = useRef(null);
  const closeButtonRef = useRef();

  const clearSearchQuery = () => {
    onChange('');
    inputRef.current.focus();
  };

  const resetSearchSuggestions = () => {
    setIsOpen(false);
    setFocusIndex(-1);
  };

  const handleAutosuggest = (e, value) => {
    resetSearchSuggestions();
    handleSubmit(e, value);
    setIsAutosuggestEnabled(false); // disable autosuggest, otherwise it shows when value is updated
  };

  const boldResult = (result) => {
    return result.replace(new RegExp(value, 'gi'), (match) => `<b>${match}</b>`);
  };

  function getMap() {
    if (!suggestionsRef.current) {
      // Initialize the Map on first usage.
      // eslint-disable-next-line no-undef
      suggestionsRef.current = new Map();
    }
    return suggestionsRef.current;
  }

  const handleNavigation = (e) => {
    let tempIndex = focusIndex;
    switch (e.key) {
      case 'Enter':
        if (closeButtonRef.current === document.activeElement) {
          e.preventDefault();
          clearSearchQuery();
          inputRef.current.focus();
        } else if (isOpen) {
          const map = getMap();
          const node = map.get(tempIndex);
          if (node) handleAutosuggest(e, node.getAttribute('aria-label'));
        } else {
          handleSubmit(e, inputRef.current.value);
        }
        break;
      case 'Escape':
        if (isOpen) {
          setIsOpen(false);
          setFocusIndex(-1);
        }
        break;
      case 'ArrowUp':
        if (tempIndex <= 0) {
          setFocusIndex(searchSuggestions.length - 1);
          break;
        }
        setFocusIndex(tempIndex - 1);
        break;
      case 'ArrowDown':
        if (tempIndex >= searchSuggestions.length - 1) {
          setFocusIndex(0);
          break;
        }
        setFocusIndex(tempIndex + 1);
        break;
      default:
    }
  };

  const handleSubmit = (e, value) => {
    e.preventDefault();
    onChange(value);
    onSubmit(value);
  };

  const handleFocus = (e) => {
    setFocusIndex(-1);
    onFocus(e);
  };

  const handleInput = (e) => {
    onInput(e);
  };

  const handleInputTextChange = (e) => {
    // reenable autosuggest when input text is changed
    if (isTypeAhead && !isAutosuggestEnabled) {
      setIsAutosuggestEnabled(true);
    }
    onChange(e.target.value);
  };

  let submitBtnIcon = (
    <LdsIcon
      name="MagnifyingGlass"
      inline
      decorative
    />
  );
  if (isLoading) {
    submitBtnIcon = (
      <LdsLoadingSpinner
        decorative
        size={21}
      />
    );
  }

  useEffect(() => {
    if (value) {
      setFocusIndex(-1);
      setIsOpen(true);
    } else {
      resetSearchSuggestions();
    }
  }, [value]);

  useOutsideClick(inputRef, () => {
    resetSearchSuggestions();
  });

  return (
    <form
      className={cn(
        'lds-search',
        {
          'lds-search-typeahead': isTypeAhead,
        },
        className
      )}
      id={`${id}Form`}
      onKeyDown={isTypeAhead ? handleNavigation : null}
      onSubmit={(e) => handleSubmit(e, inputRef.current.value)}
      ref={ref}
      role="search"
      {...rest}
    >
      <SearchInput
        autofocus={autofocus}
        className={searchInputClassName}
        disabled={disabled}
        focusIndex={focusIndex}
        handleFocus={handleFocus}
        handleInput={handleInput}
        inputAriaLabel={inputAriaLabel}
        inputId={id}
        isOpen={isOpen}
        isTypeAhead={isTypeAhead}
        placeholder={placeholder}
        value={value}
        onChange={handleInputTextChange}
        ref={inputRef}
        {...searchInputAttributes}
      />
      {isTypeAhead && isAutosuggestEnabled && isOpen && (
        <ul
          aria-label={suggestionsAriaLabel}
          className="lds-search-suggestions"
          id="searchSuggestions"
          role="listbox"
        >
          {searchSuggestions.map((result, index) => (
            <li className="lds-search-suggestion" key={index}>
              <button
                aria-label={result}
                aria-selected={index === focusIndex ? true : null}
                className={cn(
                  'lds-button',
                  'system',
                  {
                    active: index === focusIndex,
                  }
                )}
                id={`suggestion-${index}`}
                onClick={(e) => handleAutosuggest(e, result)}
                ref={(node) => {
                  const map = getMap();
                  if (node) {
                    map.set(index, node);
                  } else {
                    map.delete(index);
                  }
                }}
                role="option"
                tabIndex="-1"
              >
                <span
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: boldResult(result) }}
                />
                <span
                  className="sr-only"
                >{result}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {isTypeAhead && isAutosuggestEnabled && (
        <span id="search-helper" className="sr-only">{inputAriaDescription}</span>
      )}
      <button
        aria-hidden={!value}
        aria-label={clearBtnAriaLabel}
        className="lds-button icon-button system lds-search-clear"
        onClick={() => clearSearchQuery()}
        ref={closeButtonRef}
        style={value ? {} : { display: 'none' }}
        type="button"
      >
        <LdsIcon
          name="X"
          inline
          decorative
        />
      </button>
      <LdsButton
        aria-label={isLoading ? loadingBtnAriaLabel : submitBtnAriaLabel}
        classes="icon-button system lds-search-button"
        disabled={isLoading ? true : disabled}
        type={isLoading ? 'button' : 'submit'}
        onClick={(e) => handleSubmit(e, inputRef.current.value)}
      >
        {submitBtnIcon}
      </LdsButton>
    </form>
  );
});

LdsSearch.propTypes = {
  autofocus: PropTypes.bool,
  classes: PropTypes.string,
  clearBtnAriaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inputAriaDescription: PropTypes.string,
  inputAriaLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  isTypeAhead: PropTypes.bool,
  loadingBtnAriaLabel: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onInput: PropTypes.func,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  searchSuggestions: PropTypes.array,
  submitBtnAriaLabel: PropTypes.string,
  suggestionsAriaLabel: PropTypes.string,
  value: PropTypes.string.isRequired,
};

export default LdsSearch;
